import { forwardRef } from 'preact/compat'
import { BRAND_CLASSES } from '@/constants/theme'
import { getLabelClasses, type LabelSize } from './formLabelUtils'

export interface DatePickerProps extends Omit<JSX.IntrinsicElements['input'], 'type'> {
    label?: string
    required?: boolean
    error?: string
    helperText?: string
    containerClassName?: string
    className?: string
    /** Label size variant */
    labelSize?: LabelSize
}

/**
 * Common DatePicker component for date selection
 * Integrates with React Hook Form via forwardRef
 * 
 * @example
 * ```tsx
 * <DatePicker
 *   label="Launch Date"
 *   required
 *   error={errors.launchDate?.message}
 *   {...register('launchDate', { required: 'Required' })}
 * />
 * ```
 */
export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
    ({ label, required, error, helperText, containerClassName, className, labelSize, ...props }, ref) => {
        const inputClasses = `
            w-full px-3 py-2 border rounded-lg
            ${BRAND_CLASSES.INPUT.NORMAL}
            [color-scheme:light] dark:[color-scheme:dark]
            focus:outline-none focus:ring-2 focus:border-transparent
            ${error 
                ? BRAND_CLASSES.INPUT.ERROR 
                : BRAND_CLASSES.FOCUS.PRIMARY
            }
            ${className || ''}
        `.trim().replace(/\s+/g, ' ')

        return (
            <div className={containerClassName}>
                {label && (
                    <label className={getLabelClasses(labelSize)}>
                        {label}
                        {required && <span className={BRAND_CLASSES.INPUT.REQUIRED}> *</span>}
                    </label>
                )}
                <input
                    ref={ref}
                    type="date"
                    className={inputClasses}
                    {...props}
                />
                {error && (
                    <p className={`mt-1 text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`}>
                        {error}
                    </p>
                )}
                {!error && helperText && (
                    <p className={`mt-1 text-sm ${BRAND_CLASSES.INPUT.HELPER}`}>
                        {helperText}
                    </p>
                )}
            </div>
        )
    }
)

DatePicker.displayName = 'DatePicker'
